'use client';

/**
 * Descout per-chain dashboard — app shell (TopBar + Sidebar + content + Footer).
 *
 * Recreated from the prototype `docs/design/descout-dashboard-prototype/app/shell.jsx`
 * (visual source of truth) on the live stack: Tailwind + lucide-react + framer-motion +
 * the shared atoms in `./atoms.tsx`. The prototype's `window.*` / babel structure is
 * intentionally NOT copied — only the visual output.
 *
 * This is the ONLY client island under `(hub)/[chain]/layout.tsx`. All server data
 * (active chain, chain registry, the entity mini-list) is passed DOWN as props so this
 * file never imports a server-only module (tagService, firebase-admin, …).
 *
 * Layout decisions (from the founder chat — chats/chat1.md):
 *   - Entities is a normal gold-accented nav ROW with a small "CORE" tag, NOT a floating
 *     hero card (the floating CORE block was explicitly rejected).
 *   - Submit + Sign-in live in the sidebar FOOTER (both non-functional in slice 1).
 *   - Content fills the viewport beside the rail but is capped at 1760px (centered
 *     beyond) via a single shell-level content container — NOT a narrow per-page column.
 *   - Top bar = wordmark + chain switcher + search (+ preserved ThemeToggle /
 *     AnnouncementIndicator chrome so no existing feature is lost).
 */

import {
  useState,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
  type CSSProperties,
} from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';

import ThemeToggle from '@/components/ui/ThemeToggle';
import AnnouncementIndicator from '@/components/ui/AnnouncementIndicator';
import GlobalSearchModal from '@/components/layout/GlobalSearchModal';
import DirectorySearch from '@/components/ui/DirectorySearch';
import PriceChange from '@/components/hub/PriceChange';
import type { ChainMeta } from '@/lib/chains';
import { TagType, type EntityType, type Tag } from '@/types/tags';

import { Icon, Eyebrow, Mono, useIsMobile } from './atoms';
import DashboardFooter from './DashboardFooter';
import { EntityProfileProvider, useEntityProfileOpener } from './EntityPreviewDrawer';
import { fmtCompactUsd, initials } from './format';

/* ─────────────────────────────────────────────────────────────────────────────
   Types
───────────────────────────────────────────────────────────────────────────── */

/** Lightweight entity projection passed from the server layout for ⌘K search. */
export interface ShellEntity {
  slug: string;
  name: string;
  handle?: string;
  type?: string;
  /** Primary entity image (⌘K result thumbnails). */
  image?: string;
  /** Short description snippet (⌘K description matching/snippets). */
  description?: string;
}

/**
 * Live chain stats for the TopBar's inline context run (formerly the separate
 * ChainContextBar band). Real-data-only: a missing feed simply omits its field.
 */
export interface ChainStats {
  /** Live TVL (defi_ecosystem feed). Omitted when the feed isn't live. */
  tvl?: number;
  /** 24h TVL percent change. */
  tvlChange24h?: number;
  /** Net sentiment label from the latest daily recap (e.g. "bullish"). */
  sentiment?: string;
}

export interface DashboardShellProps {
  /**
   * The active chain (resolved + guarded server-side), or `null` for GLOBAL
   * scope — the chain-agnostic shell pages (e.g. `/read`). When null: the
   * switcher shows "All ecosystems", the stats run is hidden, and rail items
   * point at the global surfaces (locked decision 4).
   */
  chain: ChainMeta | null;
  /** Full chain registry (passed as a prop — never imported in this client file). */
  chains: ChainMeta[];
  /** Active-chain entity mini-list for the ⌘K palette. */
  entities: ShellEntity[];
  /** Server-loaded chain stats for the TopBar context run (real-data-only). */
  stats?: ChainStats;
  /**
   * ADDITIVE override for the TopBar section segment. Default `undefined` — every
   * existing consumer keeps the URL-derived NAV label (`· MARKET` etc.) with zero
   * behavior change. When set (e.g. the entity dossier), the segment renders
   * `· {section} · {detail}` verbatim instead of deriving from the path — the only
   * way to show a value (a specific entity name) the URL→NAV map can't produce.
   */
  sectionOverride?: { section: string; detail?: string };
  children: ReactNode;
}

/* ─────────────────────────────────────────────────────────────────────────────
   NAV model — all rows are live (Overview · Entities · Market · Social · News).
───────────────────────────────────────────────────────────────────────────── */

type IconName = 'overview' | 'entities' | 'market' | 'social' | 'read';

interface NavItem {
  id: string;
  label: string;
  icon: IconName;
  /** Slice-1 live (a real <Link>) vs. disabled "soon" row. */
  enabled: boolean;
  /** Entities gets the subtle gold accent (the "CORE" text badge was removed). */
  core?: boolean;
}

const NAV: NavItem[] = [
  { id: 'overview', label: 'Overview', icon: 'overview', enabled: true },
  { id: 'entities', label: 'Entities', icon: 'entities', enabled: true, core: true },
  { id: 'market', label: 'Market', icon: 'market', enabled: true },
  { id: 'social', label: 'Social', icon: 'social', enabled: true },
  { id: 'read', label: 'Read', icon: 'read', enabled: true },
];

/** Global-scope href map (chain === null). `/` is the MARKETING homepage now — the
    old `/`→`/abstract` 307 died 2026-07-09 when the money path reclaimed the root —
    so NOTHING in the product shell points at `/`. Overview routes straight to the
    PRIMARY chain's hub (`/abstract`); Read is genuinely global (`/read`).
    Entities/Market/Social have no global aggregate surface in the single-chain
    stopgap, so at global scope they also route to the PRIMARY chain (`/abstract/...`)
    — same routing middleware applies to the retired `/tags` `/market` `/social`
    standalone pages. Clicking these lands the user in Abstract chain scope, where
    navTarget's chain-scoped active tests highlight correctly.
    isActive is an exact-prefix match on the trailing-slash-stripped pathname
    (Overview = exact hub root only).
    TODO(multichain): when chain #2 ships, point these back at real global aggregate
    surfaces (reclaiming `/tags` as the all-ecosystem directory). */
const GLOBAL_HREF: Record<string, string> = {
  overview: '/abstract',
  entities: '/abstract/entities',
  market: '/abstract/market',
  social: '/abstract/social',
  read: '/read',
};

/**
 * Build a nav item's href + active test.
 *
 * Chain scope (`hubSlug` non-null): hrefs relative to the chain's hub root.
 * Global scope (`hubSlug === null`): hrefs from GLOBAL_HREF with exact-prefix
 * active matching (Overview = exact hub root only).
 */
function navTarget(item: NavItem, hubSlug: string | null) {
  if (hubSlug === null) {
    const href = GLOBAL_HREF[item.id];
    if (item.id === 'overview') {
      // Exact match on the primary hub root only — mirrors the chain-scope test
      // (`path === root`) so `/abstract/market` etc. never light Overview.
      return { href, isActive: (path: string) => path === href };
    }
    if (item.id === 'read') {
      // The Read facets (F6) live at their own global URLs but are content TYPES of
      // Read, so the rail's Read item stays active and the TopBar reports section="Read".
      const READ_FACETS = [
        '/read',
        '/articles',
        '/news',
        '/breaking',
        '/guides',
        '/analysis',
        '/daily',
        '/weekly',
      ];
      return {
        href,
        isActive: (path: string) =>
          READ_FACETS.some((f) => path === f || path.startsWith(`${f}/`)),
      };
    }
    return {
      href,
      isActive: (path: string) => path === href || path.startsWith(`${href}/`),
    };
  }
  const root = `/${hubSlug}`;
  if (item.id === 'overview') {
    return { href: root, isActive: (path: string) => path === root };
  }
  if (item.id === 'entities') {
    return {
      href: `${root}/entities`,
      // Entity dossiers live at the GLOBAL `/tags/[slug]` URL (never chain-scoped —
      // locked decision) but render inside the chain-scoped shell. So Entities reads
      // active for both the chain directory (`/<hub>/entities`) AND any `/tags/*`
      // dossier, keeping the rail coherent when a dossier is open in chain scope.
      isActive: (path: string) =>
        path.endsWith('/entities') || path === '/tags' || path.startsWith('/tags/'),
    };
  }
  if (item.id === 'market') {
    return {
      href: `${root}/market`,
      isActive: (path: string) => path.endsWith('/market'),
    };
  }
  if (item.id === 'social') {
    return {
      href: `${root}/social`,
      isActive: (path: string) => path.endsWith('/social'),
    };
  }
  if (item.id === 'read') {
    return {
      href: `${root}/read`,
      isActive: (path: string) =>
        path.endsWith('/read') || path.endsWith('/news'),
    };
  }
  // Fallback (no rows are disabled now) — no href / active state.
  return { href: undefined as string | undefined, isActive: () => false };
}

/* ─────────────────────────────────────────────────────────────────────────────
   ChainSwitcher — global ecosystem-context dropdown.
───────────────────────────────────────────────────────────────────────────── */

/**
 * The switcher's option list: covered-or-candidate ecosystems only, in registry order.
 * The `chains` prop carries the FULL registry, most of which has no business in an
 * ecosystems lens — cross-chain homes (Ethereum) and the NFT-collection chains
 * (Ronin, Polygon, …) are carried for tagging and the collection form, not coverage.
 * Candidates show from the moment they're declared, grayed as "Soon" until their
 * `status` leaves 'planned' (Lam, 07-17: show the pipeline even while it's one deep).
 */
function ecosystemOptions(chains: ChainMeta[]): ChainMeta[] {
  return chains.filter((c) => c.ecosystem);
}

/** One switcher row's box — shared by the "All ecosystems" row and the chain rows. */
const switcherRowStyle = (isOn: boolean, disabled = false): CSSProperties => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 8px',
  borderRadius: 4,
  background: isOn ? 'var(--amber-dim)' : 'transparent',
  border: 'none',
  textAlign: 'left',
  opacity: disabled ? 0.45 : 1,
  textDecoration: 'none',
});

const CHAIN_STATUS_TAG: Record<
  ChainMeta['status'],
  { label: string; color: string }
> = {
  live: { label: 'Live', color: 'var(--green-text)' },
  mvc: { label: 'New', color: 'var(--amber)' },
  planned: { label: 'Soon', color: 'var(--slate)' },
};

/**
 * Chain identity glyph — the chain's brand logo PNG when available, else the
 * brand-color dot. Rendered in a fixed square slot so labels stay aligned whether
 * a logo or the dot fallback shows. Falls back to the dot on image error too:
 * several chains carry a `logo` path whose PNG doesn't exist yet.
 */
function ChainGlyph({
  chain,
  size = 18,
  glow = false,
}: {
  chain: ChainMeta;
  size?: number;
  glow?: boolean;
}) {
  const [errored, setErrored] = useState(false);
  const showLogo = !!chain.logo && !errored;
  const dot = Math.round(size / 2);

  return (
    <span
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {showLogo ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={chain.logo}
          alt=""
          width={size}
          height={size}
          onError={() => setErrored(true)}
          // A server-rendered <img> can 404 BEFORE React attaches onError (the native
          // error fires pre-hydration), so also detect an already-failed image at mount.
          ref={(node) => {
            if (node && node.complete && node.naturalWidth === 0) setErrored(true);
          }}
          style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
        />
      ) : (
        <span
          style={{
            width: dot,
            height: dot,
            borderRadius: '50%',
            background: chain.color,
            boxShadow: glow ? `0 0 8px ${chain.color}66` : undefined,
          }}
        />
      )}
    </span>
  );
}

function ChainSwitcher({
  chains,
  active,
}: {
  chains: ChainMeta[];
  /** null = global scope (no active chain) → "All ecosystems" trigger. */
  active: ChainMeta | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Global scope: neutral dot (no chain color), rows jump into a chain's Read.
  const global = active === null;

  useEffect(() => {
    const handler = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const options = ecosystemOptions(chains);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 9,
          cursor: 'pointer',
          background: 'var(--surface)',
          border: '0.5px solid var(--divider)',
          borderRadius: 4,
          padding: '6px 10px 6px 11px',
          color: 'var(--parchment)',
        }}
      >
        {global ? (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'var(--slate)',
              flexShrink: 0,
            }}
          />
        ) : (
          <ChainGlyph chain={active} size={16} glow />
        )}
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: '-0.01em',
          }}
        >
          {global ? 'All ecosystems' : active.label}
        </span>
        <span style={{ color: 'var(--slate)' }}>
          <Icon name="chevron" size={14} />
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            width: 248,
            zIndex: 60,
            background: 'var(--surface)',
            border: '0.5px solid var(--divider)',
            borderRadius: 6,
            padding: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ padding: '6px 8px 8px' }}>
            <Eyebrow color="var(--slate)">Ecosystem context</Eyebrow>
          </div>
          {/* The way back out of a hub: chain-scoped nav rows are all hub-scoped
              (Read → /<hub>/read), so without this row the cross-ecosystem Read is
              unreachable from inside an ecosystem. Pinned first, mirroring the
              trigger's own global-scope state (neutral dot, no status tag). */}
          <Link
            href="/read"
            onClick={() => setOpen(false)}
            style={{ ...switcherRowStyle(global), cursor: 'pointer' }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: 'var(--slate)',
                }}
              />
            </span>
            <span
              style={{
                flex: 1,
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 13,
                color: 'var(--parchment)',
              }}
            >
              All ecosystems
            </span>
          </Link>

          {options.map((c) => {
            const tag = CHAIN_STATUS_TAG[c.status];
            const isOn = !global && c.id === active.id;
            const disabled = c.status === 'planned';
            const rowStyle = switcherRowStyle(isOn, disabled);
            const inner = (
              <>
                <ChainGlyph chain={c} size={18} />
                <span
                  style={{
                    flex: 1,
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 600,
                    fontSize: 13,
                    color: 'var(--parchment)',
                  }}
                >
                  {c.label}
                </span>
                <Mono
                  size={9.5}
                  color={tag.color}
                  style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}
                >
                  {tag.label}
                </Mono>
              </>
            );

            if (disabled) {
              return (
                <div
                  key={c.id}
                  aria-disabled
                  style={{ ...rowStyle, cursor: 'not-allowed' }}
                >
                  {inner}
                </div>
              );
            }

            return (
              <Link
                key={c.id}
                // Global scope: rows jump into a chain's Read (the only global
                // shell page with a chain twin in v1). Chain scope: the hub root.
                href={global ? `/${c.hubSlug}/read` : `/${c.hubSlug}`}
                onClick={() => setOpen(false)}
                style={{ ...rowStyle, cursor: 'pointer' }}
              >
                {inner}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SearchPalette (⌘K) — chain-scoped entity quick-jump + bridge to global search.

   Unified on the DirectorySearch convention (the GlobalSearchModal pattern):
   scored multi-field matching, match-context badges, <mark> highlighting,
   arrow-key + Enter navigation, real-link results, 150ms debounce — rendered
   with the token-native 'dashboard' skin. The shell owns ⌘K (hotkey={false}
   here); a pick opens the in-shell profile drawer via useEntityProfileOpener.
───────────────────────────────────────────────────────────────────────────── */

/** Palette epoch — stable Date so the Tag mapping below is referentially calm. */
const PALETTE_EPOCH = new Date(0);

/**
 * Adapt the slim server-provided ShellEntity projection into the Tag shape the
 * DirectorySearch engine consumes. Only search-relevant fields are real; the
 * required Tag boilerplate (parent/type/isActive/dates) is inert filler.
 */
function toPaletteTag(e: ShellEntity): Tag {
  return {
    id: e.slug,
    name: e.name,
    slug: e.slug,
    description: e.description,
    parent: null,
    type: TagType.CHILD,
    isActive: true,
    createdAt: PALETTE_EPOCH,
    updatedAt: PALETTE_EPOCH,
    entityType: (e.type as EntityType) ?? null,
    twitterHandles: e.handle ? [e.handle] : undefined,
    images: e.image ? [{ url: e.image, isPrimary: true }] : undefined,
    // Required analytics/path boilerplate — inert for palette search.
    articleCount: 0,
    breakingNewsCount: 0,
    viewCount: 0,
    trending: false,
    weight: 0,
    fullPath: e.slug,
  };
}

function SearchPalette({
  open,
  onClose,
  entities,
  onOpenGlobal,
}: {
  open: boolean;
  onClose: () => void;
  entities: ShellEntity[];
  onOpenGlobal: () => void;
}) {
  const router = useRouter();
  // In-shell entity profile (bet F) — a ⌘K pick opens the profile DRAWER instead of
  // bouncing out to /tags; falls back to navigation if the provider is absent.
  const openProfile = useEntityProfileOpener();

  const paletteTags = useMemo(() => entities.map(toPaletteTag), [entities]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const pick = (tag: Tag) => {
    onClose();
    if (openProfile) openProfile(tag.slug);
    else router.push(`/tags/${tag.slug}`);
  };

  return (
    <div
      onMouseDown={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(8,8,8,0.62)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '12vh',
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 'min(620px,92vw)',
          background: 'var(--surface)',
          border: '0.5px solid var(--divider)',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
        }}
      >
        {/* The unified search engine (DirectorySearch, dashboard skin). Results
            render IN FLOW (inlineResults) so the footer bridge below stays a
            proper palette footer instead of being overlapped by a dropdown. */}
        <div style={{ padding: 14 }}>
          <DirectorySearch
            tags={paletteTags}
            mode="navigate"
            skin="dashboard"
            hotkey={false}
            autoFocus
            inlineResults
            idleResults={7}
            placeholder="Search entities, people, tokens…"
            onTagActivate={pick}
          />
        </div>

        {/* No-feature-loss bridge — hand off to the existing global search. */}
        <button
          type="button"
          onClick={() => {
            onClose();
            onOpenGlobal();
          }}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 18px',
            cursor: 'pointer',
            background: 'transparent',
            border: 'none',
            borderTop: '0.5px solid var(--divider)',
            textAlign: 'left',
          }}
          onMouseEnter={(ev) => {
            ev.currentTarget.style.background = 'var(--parchment-faint)';
          }}
          onMouseLeave={(ev) => {
            ev.currentTarget.style.background = 'transparent';
          }}
        >
          <span style={{ color: 'var(--amber)' }}>
            <Icon name="search" size={15} />
          </span>
          <Mono size={12} color="var(--parchment)">
            Search all of Descout
          </Mono>
          <span style={{ flex: 1 }} />
          <span style={{ color: 'var(--slate)' }}>
            <Icon name="arrowUpRight" size={14} />
          </span>
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   TopBar — sticky 56px chrome.

   The chain-context stats (bet F) live INLINE here (Lam: Option A), to the
   right of the ChainSwitcher — the separate 36px ChainContextBar band is gone.
   Visual language is the bar's original: mono uppercase mid-dot run, chain
   color stays ONLY on the switcher's dot, real-data-only (missing stats are
   OMITTED, never zero-filled).
───────────────────────────────────────────────────────────────────────────── */

/** Tone the sentiment label: bull → green, bear → red, else neutral dim. */
function sentimentTone(sentiment: string): string {
  const s = sentiment.toLowerCase();
  if (s.includes('bull')) return 'text-signal-green-text';
  if (s.includes('bear')) return 'text-alert-red-text';
  return 'text-parchment-dim';
}

/** Mono mid-dot separator between stats (lives INSIDE each stat segment so a
    responsive-hidden segment takes its separator with it). */
function StatSep() {
  return (
    <span className="mx-2.5 flex-shrink-0 text-divider" aria-hidden>
      ·
    </span>
  );
}

/**
 * Inline chain stats run — TVL + 24h Δ · N entities · sentiment.
 *
 * Squeeze ladder (viewport): <1180px drops sentiment → <1024px drops entities
 * → TVL survives until the 820px mobile breakpoint, below which the whole run
 * is hidden (selector only). CSS-driven (`min-[…]`) so SSR'd HTML degrades
 * correctly before hydration.
 */
function ChainStatsRun({
  chain,
  stats,
  entityCount,
}: {
  chain: ChainMeta;
  stats?: ChainStats;
  entityCount: number;
}) {
  const hasTvl = typeof stats?.tvl === 'number' && stats.tvl > 0;
  const hasEntities = entityCount > 0;
  const sentiment = stats?.sentiment;
  // Real-data-only: nothing live → render nothing (identity already lives on
  // the ChainSwitcher).
  if (!hasTvl && !hasEntities && !sentiment) return null;

  return (
    <div
      data-testid="topbar-chain-stats"
      aria-label={`${chain.label} chain stats`}
      className="hidden min-w-0 items-center overflow-hidden whitespace-nowrap font-mono text-[10.5px] uppercase tracking-[0.08em] text-parchment-dim min-[821px]:flex"
    >
      {hasTvl && (
        <span className="flex items-center">
          <StatSep />
          <span className="text-parchment">{fmtCompactUsd(stats!.tvl!)}</span>
          <span className="ml-1">TVL</span>
          {typeof stats?.tvlChange24h === 'number' && (
            <PriceChange value={stats.tvlChange24h} className="ml-1.5 text-[10px]" />
          )}
        </span>
      )}
      {hasEntities && (
        <span className="hidden items-center min-[1024px]:flex">
          <StatSep />
          <span className="text-parchment">{entityCount}</span>
          <span className="ml-1">entities</span>
        </span>
      )}
      {sentiment && (
        <span className="hidden items-center min-[1180px]:flex">
          <StatSep />
          <span>sentiment</span>
          <span className={`ml-1 font-semibold ${sentimentTone(sentiment)}`}>
            {sentiment}
          </span>
        </span>
      )}
    </div>
  );
}

function Wordmark({ mobile }: { mobile: boolean }) {
  // Deterministic box on BOTH axes (the Navbar/DashboardFooter pattern, 620c667):
  // `w-auto` tracked the optimizer-rounded srcset ratio (128x21), tripping
  // next/image's width-XOR-height dev warning. Fixed widths match the h-4/h-5 box.
  const size = mobile ? 'h-4 w-[96px]' : 'h-5 w-[120px]';
  // Inside the product, "home" = the desk (the primary hub), NOT the `/` marketing
  // homepage — the old `/`→`/abstract` 307 is gone (Lam, 07-09).
  return (
    <Link
      href="/abstract/"
      className="flex items-center flex-shrink-0"
      aria-label="Descout — the desk"
    >
      <Image
        src="/descout-logo-dark.png"
        alt="Descout"
        width={120}
        height={20}
        className={`${size} hidden dark:block`}
        priority
      />
      <Image
        src="/descout-logo-light.png"
        alt="Descout"
        width={120}
        height={20}
        className={`${size} block dark:hidden`}
        priority
      />
    </Link>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   AccountChip — the ONE signed-in account affordance in the public shell.

   This is how a signed-in contributor reaches their private home (/you). It is
   deliberately NOT a browse nav item — the public browse nav (Overview · Entities
   · … · Open Questions) is untouched. An ACCOUNT chip lives in the header chrome.

   DEFENSIVE BY DESIGN: DashboardShell renders on many routes that have NO
   AuthProvider ancestor (Overview, Entities, Market, …), so this chip can't use
   useAuth() — that hook throws outside a provider. Instead it subscribes DIRECTLY
   to Firebase auth (the same primitive AuthProvider uses), so it never crashes the
   shell. Renders NOTHING while loading or signed out; a small avatar/"You" chip
   linking to /you once a user is present.
───────────────────────────────────────────────────────────────────────────── */

/** Firestore enrichment for the chip — handle + role + the authoritative avatar. Cached at
 *  module level so it's fetched ONCE per session (uid), never per navigation. */
type ChipProfile = { profileImage: string; twitterUsername: string; role: string };
const chipProfileCache = new Map<string, ChipProfile>();

// Exported as a test seam (AccountChip.states.test.tsx) — internal to the shell otherwise.
export function AccountChip({ mobile }: { mobile: boolean }) {
  // {undefined} = still resolving auth (render nothing); null = signed out; object = signed in.
  const [authedUser, setAuthedUser] = useState<
    { uid: string; displayName: string; photoURL: string } | null | undefined
  >(undefined);
  // Firestore enrichment (handle/role/avatar). null until fetched — the chip renders on
  // Firebase basics first and refines when this arrives (never blocks on Firestore).
  const [profile, setProfile] = useState<ChipProfile | null>(null);
  const [imgBroken, setImgBroken] = useState(false);

  useEffect(() => {
    // Lazily reach Firebase auth so SSR never touches it and a misconfigured client (no auth)
    // degrades to "no chip" instead of throwing. onAuthStateChanged is the same source the
    // AuthProvider uses — this just reads it without requiring the React context.
    let unsub: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      try {
        const { auth } = await import('@/lib/firebase');
        const { onAuthStateChanged } = await import('firebase/auth');
        if (!auth) {
          if (!cancelled) setAuthedUser(null);
          return;
        }
        unsub = onAuthStateChanged(auth, (u) => {
          if (cancelled) return;
          setAuthedUser(
            u
              ? { uid: u.uid, displayName: u.displayName ?? 'You', photoURL: u.photoURL ?? '' }
              : null,
          );
        });
      } catch {
        if (!cancelled) setAuthedUser(null);
      }
    })();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  const uid = authedUser?.uid;
  useEffect(() => {
    if (!uid) {
      setProfile(null);
      return;
    }
    const cached = chipProfileCache.get(uid);
    if (cached) {
      setProfile(cached);
      return;
    }
    // Fetch the user doc ONCE per session (same defensive/lazy pattern as the auth read):
    // any failure degrades to Firebase-only identity, never crashes the shell.
    let cancelled = false;
    (async () => {
      try {
        const { db } = await import('@/lib/firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        if (!db) return;
        const snap = await getDoc(doc(db, 'users', uid));
        if (cancelled) return;
        const d = snap.exists() ? (snap.data() as Record<string, unknown>) : {};
        const p: ChipProfile = {
          profileImage:
            (d.profileImage as string) || (d.twitterProfileImageUrl as string) || '',
          twitterUsername: (d.twitterUsername as string) || '',
          role: (d.role as string) || '',
        };
        chipProfileCache.set(uid, p);
        setProfile(p);
      } catch {
        // fail-silent — the chip keeps the Firebase-only identity
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const avatarSrc = profile?.profileImage || authedUser?.photoURL || '';
  useEffect(() => {
    setImgBroken(false); // a new src gets a fresh chance before we fall back to initials
  }, [avatarSrc]);

  // Loading or signed out → no affordance (keeps the header clean; sign-in lives in the
  // contribute flow's inline gate, reached via the Submit/board CTAs). BYTE-IDENTICAL to
  // before for signed-out users: renders nothing.
  if (!authedUser) return null;

  const name = authedUser.displayName || 'You';
  const handle = profile?.twitterUsername ? `@${profile.twitterUsername}` : '';
  // Role kicker — Operator for staff-world owner/admin, Contributor otherwise (member and
  // contributor both read as Contributor in the public shell chip).
  const kicker = profile?.role === 'owner' || profile?.role === 'admin' ? 'Operator' : 'Contributor';
  const avatarSize = mobile ? 28 : 30;
  const showAvatar = avatarSrc && !imgBroken;

  return (
    <Link
      href="/you"
      title="Your home"
      aria-label={`Your home — signed in as ${name}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: mobile ? 0 : 9,
        flexShrink: 0,
        cursor: 'pointer',
        background: 'var(--surface)',
        border: '0.5px solid var(--divider)',
        borderRadius: 999,
        padding: mobile ? 2 : '2px 12px 2px 2px',
        color: 'var(--parchment)',
        textDecoration: 'none',
      }}
    >
      {showAvatar ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={avatarSrc}
          alt=""
          width={avatarSize}
          height={avatarSize}
          onError={() => setImgBroken(true)}
          style={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: '50%',
            objectFit: 'cover',
            flexShrink: 0,
            border: '0.5px solid var(--divider)',
          }}
        />
      ) : (
        <span
          aria-hidden
          style={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: '50%',
            background: 'var(--clr-scout-gold-dim)',
            color: 'var(--clr-scout-gold)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-sans)',
            fontSize: 11,
            fontWeight: 700,
            flexShrink: 0,
            border: '0.5px solid var(--divider)',
          }}
        >
          {initials(name)}
        </span>
      )}
      {!mobile && (
        <span
          style={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            lineHeight: 1.1,
          }}
        >
          <span
            style={{
              maxWidth: 132,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 12.5,
            }}
          >
            {name}
          </span>
          <span
            style={{
              maxWidth: 132,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--parchment-dim)',
              marginTop: 1.5,
            }}
          >
            {kicker}
            {handle ? ` · ${handle}` : ''}
          </span>
        </span>
      )}
    </Link>
  );
}

function TopBar({
  chain,
  chains,
  stats,
  entityCount,
  onSearch,
  mobile,
  sectionOverride,
}: {
  /** null = global scope (the `/read` shell page). */
  chain: ChainMeta | null;
  chains: ChainMeta[];
  stats?: ChainStats;
  entityCount: number;
  onSearch: () => void;
  mobile: boolean;
  sectionOverride?: { section: string; detail?: string };
}) {
  // Derive the current section label from the URL against the NAV model so the
  // TopBar shows `● Abstract · MARKET` etc. Strip the trailing slash to match
  // navTarget's active tests (`trailingSlash: true`). null on a non-NAV path.
  const hubSlug = chain?.hubSlug ?? null;
  const pathname = (usePathname() ?? '').replace(/\/+$/, '');
  // An explicit override wins (the dossier needs `· ENTITIES · {NAME}`, which the
  // URL→NAV map can't produce). Otherwise fall back to the URL-derived NAV label.
  const section =
    sectionOverride?.section ??
    NAV.map((item) => ({ item, t: navTarget(item, hubSlug) })).find(({ t }) =>
      t.isActive(pathname),
    )?.item.label ??
    null;
  const sectionDetail = sectionOverride?.detail ?? null;

  return (
    <header
      style={{
        height: 56,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: mobile ? 10 : 16,
        padding: mobile ? '0 12px' : '0 18px',
        borderBottom: '0.5px solid var(--divider)',
        background: 'rgba(13,13,13,0.86)',
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      <Wordmark mobile={mobile} />
      {!mobile && (
        <span style={{ width: 1, height: 22, background: 'var(--divider)' }} />
      )}
      <ChainSwitcher chains={chains} active={chain} />
      {/* Section segment — the page title moved out of per-page headers into the
          TopBar (spec §F). Mono uppercase, mirrors the ChainStatsRun mid-dot run.
          Truncates and is hidden below `sm:` so it never pushes the stats off. */}
      {section && (
        <span className="hidden min-w-0 items-center gap-2 sm:flex">
          <span
            aria-hidden
            className="flex-shrink-0"
            style={{ color: 'var(--slate)' }}
          >
            ·
          </span>
          <span className="truncate font-mono text-[10.5px] uppercase tracking-[0.12em] text-parchment">
            {section}
            {sectionDetail && (
              <>
                <span className="mx-1.5 text-slate-ds" aria-hidden>
                  ·
                </span>
                {sectionDetail}
              </>
            )}
          </span>
        </span>
      )}
      {/* Inline chain-context stats (formerly the separate ChainContextBar band).
          Hidden entirely below the 820 mobile breakpoint — selector only. Global
          scope (chain === null) renders no stats run. */}
      {!mobile && chain && (
        <ChainStatsRun chain={chain} stats={stats} entityCount={entityCount} />
      )}
      <div style={{ flex: 1 }} />

      {mobile ? (
        <button
          type="button"
          onClick={onSearch}
          aria-label="Search"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            background: 'var(--surface)',
            border: '0.5px solid var(--divider)',
            borderRadius: 4,
            padding: 8,
            color: 'var(--slate)',
          }}
        >
          <Icon name="search" size={17} />
        </button>
      ) : (
        <button
          type="button"
          onClick={onSearch}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            background: 'var(--surface)',
            border: '0.5px solid var(--divider)',
            borderRadius: 4,
            padding: '7px 10px',
            color: 'var(--slate)',
            width: 230,
          }}
        >
          <Icon name="search" size={15} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13 }}>Search…</span>
          <span style={{ flex: 1 }} />
          <Mono
            size={10}
            color="var(--slate)"
            style={{
              border: '0.5px solid var(--divider)',
              borderRadius: 3,
              padding: '2px 5px',
            }}
          >
            ⌘K
          </Mono>
        </button>
      )}

      {/* Preserved existing chrome — no feature loss. */}
      <AnnouncementIndicator />
      <ThemeToggle />
      {/* The ONE account affordance: a signed-in contributor's door to their private home
          (/you). Renders nothing when signed out — never a new browse item. */}
      <AccountChip mobile={mobile} />
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Sidebar — collapsible nav rail (desktop only).
───────────────────────────────────────────────────────────────────────────── */

function Sidebar({
  chain,
  collapsed,
  onToggle,
  nav,
}: {
  /** null = global scope. */
  chain: ChainMeta | null;
  collapsed: boolean;
  onToggle: () => void;
  /** The visible nav rows (WS-K rows filtered out pre-launch by the shell). */
  nav: NavItem[];
}) {
  // Strip the trailing slash (`trailingSlash: true` → usePathname returns `/abstract/entities/`)
  // so navTarget's `=== root` / `endsWith('/…')` active tests match.
  const pathname = (usePathname() ?? '').replace(/\/+$/, '');
  const width = collapsed ? 68 : 248;
  const hubSlug = chain?.hubSlug ?? null;

  const renderItem = (item: NavItem) => {
    const { href, isActive } = navTarget(item, hubSlug);
    const active = item.enabled && isActive(pathname);
    const iconColor = item.core
      ? 'var(--amber)'
      : active
        ? 'var(--parchment)'
        : 'inherit';

    const rowStyle: CSSProperties = {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: collapsed ? '11px 0' : '10px 12px',
      justifyContent: collapsed ? 'center' : 'flex-start',
      borderRadius: 5,
      border: 'none',
      textAlign: 'left',
      textDecoration: 'none',
      background: active
        ? item.core
          ? 'var(--amber-dim)'
          : 'var(--parchment-faint)'
        : 'transparent',
      color: active ? 'var(--parchment)' : 'var(--slate)',
      position: 'relative',
      transition: 'background 160ms, color 160ms',
      opacity: item.enabled ? 1 : 0.45,
      cursor: item.enabled ? 'pointer' : 'not-allowed',
    };

    const inner = (
      <>
        {active && (
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: 8,
              bottom: 8,
              width: 2,
              background: 'var(--amber)',
              borderRadius: 2,
            }}
          />
        )}
        <span style={{ color: iconColor, flexShrink: 0 }}>
          <Icon name={item.icon} size={18} />
        </span>
        {!collapsed && (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 13.5,
                letterSpacing: '-0.01em',
              }}
            >
              {item.label}
            </span>
            {/* "CORE" badge removed (2026-06-14): an internal "this is our moat"
                label didn't belong in user nav chrome — it told instead of showed.
                Entities' second-slot position + the subtle gold accent (kept) carry
                the emphasis. `core` still drives that accent below. */}
            {!item.enabled && (
              <Mono
                size={8}
                color="var(--slate)"
                style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}
              >
                Soon
              </Mono>
            )}
          </span>
        )}
      </>
    );

    if (!item.enabled || !href) {
      return (
        <div
          key={item.id}
          aria-disabled
          title="Coming soon"
          style={rowStyle}
        >
          {inner}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        href={href}
        title={collapsed ? item.label : undefined}
        style={rowStyle}
        onMouseEnter={(e) => {
          if (!active) e.currentTarget.style.color = 'var(--parchment)';
        }}
        onMouseLeave={(e) => {
          if (!active) e.currentTarget.style.color = 'var(--slate)';
        }}
      >
        {inner}
      </Link>
    );
  };

  return (
    <aside
      style={{
        width,
        flexShrink: 0,
        borderRight: '0.5px solid var(--divider)',
        background: 'var(--ink)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 200ms cubic-bezier(0.4,0,0.2,1)',
        position: 'sticky',
        top: 56,
        height: 'calc(100vh - 56px)',
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: collapsed ? '14px 12px' : '16px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {!collapsed && (
          <div style={{ padding: '2px 12px 6px' }}>
            <Eyebrow color="var(--slate)">
              {chain ? `${chain.label} ecosystem` : 'All ecosystems'}
            </Eyebrow>
          </div>
        )}
        {nav.map(renderItem)}

        {/* Actions — Submit (disabled) + Sign in (not yet wired). `marginTop:auto` pins
            them to the bottom of the nav region, ABOVE the divider rule (per founder
            feedback). */}
        <div
          style={{
            marginTop: 'auto',
            paddingTop: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
        {/* Submit CTA — public graph-intake suggest-entity flow. */}
        <Link
          href="/submit"
          title="Suggest an entity for the graph"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            justifyContent: 'center',
            background: 'var(--amber)',
            border: '1px solid var(--amber)',
            borderRadius: 5,
            padding: collapsed ? '10px 0' : '10px 14px',
            color: 'var(--ink)',
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 13,
            textDecoration: 'none',
          }}
        >
          <Icon name="plus" size={15} />
          {!collapsed && 'Submit an entity'}
        </Link>

        {/* Sign in — non-functional placeholder. Sub-label reads "Coming soon", matching
            the disabled nav rows' "Soon" badge above; the old "Phase 2" suffix was an
            internal roadmap term leaking into reader chrome (same call as the "CORE"
            badge removal, 2026-06-14 — chrome should show, not tell). */}
        <button
          type="button"
          disabled
          title="Coming soon"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            justifyContent: collapsed ? 'center' : 'flex-start',
            cursor: 'not-allowed',
            background: 'transparent',
            border: '1px solid var(--divider)',
            borderRadius: 5,
            padding: collapsed ? '9px 0' : '8px 14px',
            color: 'var(--parchment-dim)',
            textAlign: 'left',
          }}
        >
          <span style={{ color: 'var(--slate)', flexShrink: 0 }}>
            <Icon name="signin" size={16} />
          </span>
          {!collapsed && (
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 13,
                  color: 'var(--parchment-dim)',
                }}
              >
                Sign in
              </span>
              <span
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 9,
                  letterSpacing: '0.06em',
                  color: 'var(--slate)',
                }}
              >
                Coming soon
              </span>
            </span>
          )}
        </button>
        </div>
      </div>

      {/* Footer — Data live + Collapse, below the divider rule. */}
      <div
        style={{
          borderTop: '0.5px solid var(--divider)',
          padding: collapsed ? 12 : 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {/* Data-live indicator (NO fabricated timestamp). */}
        {!collapsed && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 2,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'var(--green-text)',
                boxShadow: '0 0 6px var(--green-text)',
                flexShrink: 0,
              }}
            />
            <Mono size={10} color="var(--slate)">
              Data live
            </Mono>
          </div>
        )}

        <button
          type="button"
          onClick={onToggle}
          title={collapsed ? 'Expand' : 'Collapse'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 9,
            cursor: 'pointer',
            background: 'transparent',
            border: 'none',
            color: 'var(--slate)',
            padding: collapsed ? 0 : 2,
            justifyContent: collapsed ? 'center' : 'flex-start',
            width: '100%',
          }}
        >
          <span style={{ transform: collapsed ? 'scaleX(-1)' : 'none' }}>
            <Icon name="collapse" size={17} />
          </span>
          {!collapsed && (
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 12.5,
                fontWeight: 500,
              }}
            >
              Collapse
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MobileNav — fixed bottom bar (shown on mobile).
───────────────────────────────────────────────────────────────────────────── */

function MobileNav({ chain, nav }: { chain: ChainMeta | null; nav: NavItem[] }) {
  // Strip the trailing slash (`trailingSlash: true` → usePathname returns `/abstract/entities/`)
  // so navTarget's `=== root` / `endsWith('/…')` active tests match.
  const pathname = (usePathname() ?? '').replace(/\/+$/, '');
  const hubSlug = chain?.hubSlug ?? null;

  return (
    <nav
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'stretch',
        background: 'rgba(13,13,13,0.94)',
        backdropFilter: 'blur(10px)',
        borderTop: '0.5px solid var(--divider)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {nav.map((item) => {
        const { href, isActive } = navTarget(item, hubSlug);
        const active = item.enabled && isActive(pathname);
        const color = active
          ? item.core
            ? 'var(--amber)'
            : 'var(--parchment)'
          : 'var(--slate)';
        const cellStyle: CSSProperties = {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          padding: '9px 0 8px',
          background: 'none',
          border: 'none',
          color,
          textDecoration: 'none',
          opacity: item.enabled ? 1 : 0.45,
        };
        const inner = (
          <>
            <Icon name={item.icon} size={19} />
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 9.5,
                letterSpacing: '-0.01em',
              }}
            >
              {item.label}
            </span>
          </>
        );

        if (!item.enabled || !href) {
          return (
            <div
              key={item.id}
              aria-disabled
              title="Coming soon"
              style={{ ...cellStyle, cursor: 'not-allowed' }}
            >
              {inner}
            </div>
          );
        }
        return (
          <Link key={item.id} href={href} style={cellStyle}>
            {inner}
          </Link>
        );
      })}

      {/* Submit — disabled (coming soon). */}
      <button
        type="button"
        disabled
        title="Coming soon"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          padding: '9px 0 8px',
          cursor: 'not-allowed',
          background: 'none',
          border: 'none',
          color: 'var(--amber)',
          opacity: 0.55,
        }}
      >
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: 7,
            background: 'var(--amber)',
            color: 'var(--ink)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="plus" size={16} />
        </span>
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 9.5,
          }}
        >
          Submit
        </span>
      </button>
    </nav>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   DashboardShell — composes the chrome around the (server-rendered) {children}.
───────────────────────────────────────────────────────────────────────────── */

export default function DashboardShell({
  chain,
  chains,
  entities,
  stats,
  sectionOverride,
  children,
}: DashboardShellProps) {
  const mobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [globalOpen, setGlobalOpen] = useState(false);

  // OQ-SHAPE: the Open Questions board is now an INDEX, not a nav destination — questions live on
  // each entity's dossier (the primary surface). The sidebar row + its href/active machinery were
  // removed with this leg; the board stays reachable at `/open-questions`, from dossier permalinks,
  // and from the /contribute doorway. Nav = the live NAV rows directly.
  const visibleNav = NAV;

  // ⌘K / Ctrl+K opens the chain-scoped palette.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const shell = (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--ink)',
      }}
    >
      <TopBar
        chain={chain}
        chains={chains}
        stats={stats}
        entityCount={entities.length}
        onSearch={() => setSearchOpen(true)}
        mobile={mobile}
        sectionOverride={sectionOverride}
      />

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {!mobile && (
          <Sidebar
            chain={chain}
            collapsed={collapsed}
            onToggle={() => setCollapsed((c) => !c)}
            nav={visibleNav}
          />
        )}

        {/* Content column. Fills the viewport beside the rail, but the inner content
            container is CAPPED at 1760px (centered beyond) with consistent side padding
            — applied HERE at the shell level so EVERY sub-route (Overview, Entities,
            Market, Social, News) inherits one width/gutter source of truth (sub-routes
            no longer set their own max-w/padding). 1760px (up from 1600) matches the
            prototype's full-bleed intent: 1440–1920 viewports fill edge-to-edge beside
            the 248px rail while ultrawides stay capped + centered. The footer stays
            outside the cap so its own full-width treatment is preserved (no feature
            loss). */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            // Leave room for the fixed mobile bottom nav (its own height PLUS
            // the home-indicator safe area it pads itself with — a fixed 64px
            // left the page end tucked under the nav on notched phones).
            paddingBottom: mobile ? 'calc(64px + env(safe-area-inset-bottom))' : 0,
          }}
        >
          <div style={{ flex: 1 }}>
            <div className="mx-auto w-full max-w-[1760px] px-4 sm:px-6 lg:px-8 2xl:px-10">
              {children}
            </div>
          </div>
          <DashboardFooter />
        </main>
      </div>

      {mobile && <MobileNav chain={chain} nav={visibleNav} />}

      <SearchPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        entities={entities}
        onOpenGlobal={() => setGlobalOpen(true)}
      />
      <GlobalSearchModal open={globalOpen} onClose={() => setGlobalOpen(false)} />
    </div>
  );

  // EntityProfileProvider (bet F): the shell-level in-place entity profile. Any
  // descendant (⌘K palette, Overview rows, Social page) opens it via
  // `useEntityProfileOpener()` / `<EntityProfileLink>`; the drawer itself renders
  // once here. `/tags/[slug]` stays the crawlable canonical — the drawer is a VIEW.
  //
  // Global scope (chain === null) skips the provider — the drawer is chain-context
  // bound, so a ⌘K pick falls back to navigating to `/tags/[slug]` (the correct
  // global behavior; `useEntityProfileOpener()` returns null outside the provider).
  if (chain === null) return shell;

  return <EntityProfileProvider chain={chain}>{shell}</EntityProfileProvider>;
}
